import React, { useState, useEffect } from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Button,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material'
import {
  ArrowBack,
  Edit,
  Verified,
  Error as ErrorIcon,
  History,
  Download,
  Print,
  Calculate as CalculateIcon
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import medicalCodingService from '../services/medicalCodingService'
import mrdService from '../services/mrdservice'

export const CodingDetailsPage = () => {
  const { fileId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [mrdFile, setMrdFile] = useState(null)
  const [coding, setCoding] = useState(null)
  const [auditTrail, setAuditTrail] = useState([])
  const [versions, setVersions] = useState([])
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false)
  const [verificationData, setVerificationData] = useState({
    status: 'VERIFIED',
    remarks: ''
  })

  useEffect(() => {
    if (fileId) {
      loadData()
    }
  }, [fileId])

  const loadData = async () => {
    setLoading(true)
    try {
      const [mrdResponse, codingResponse] = await Promise.all([
        mrdService.getFileById(fileId),
        medicalCodingService.getCodingByFile(fileId)
      ])

      if (mrdResponse.data.success) {
        setMrdFile(mrdResponse.data.data)
      }

      if (codingResponse.data.success) {
        setCoding(codingResponse.data.data)
        
        // Load audit trail and versions
        if (codingResponse.data.data._id) {
          loadAuditTrail(codingResponse.data.data._id)
          loadVersions(codingResponse.data.data._id)
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to load coding details', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadAuditTrail = async (codingId) => {
    try {
      const response = await medicalCodingService.getCodingAuditTrail(codingId)
      if (response.data.success) {
        setAuditTrail(response.data.data.auditTrail || [])
      }
    } catch (error) {
      console.log('Audit trail not available')
    }
  }

  const loadVersions = async (codingId) => {
    try {
      const response = await medicalCodingService.getCodingVersions(codingId)
      if (response.data.success) {
        setVersions(response.data.data || [])
      }
    } catch (error) {
      console.log('Version history not available')
    }
  }

  const handleVerify = async () => {
    try {
      await medicalCodingService.verifyCoding(coding._id, verificationData)
      enqueueSnackbar(`Coding ${verificationData.status.toLowerCase()}`, { variant: 'success' })
      setOpenVerifyDialog(false)
      loadData()
    } catch (error) {
      enqueueSnackbar('Failed to verify coding', { variant: 'error' })
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED': return 'success'
      case 'PENDING': return 'warning'
      case 'REJECTED': return 'error'
      case 'QUERY_RAISED': return 'info'
      default: return 'default'
    }
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading coding details...
        </Typography>
      </Container>
    )
  }

  if (!coding) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info">
          No coding record found for this file.
          <Button 
            sx={{ ml: 2 }}
            onClick={() => navigate(`/coding/${fileId}/edit`)}
          >
            Create Coding
          </Button>
        </Alert>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl">
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/coding')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            Medical Coding Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mrdFile?.fileNumber} - {mrdFile?.patientName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<Edit />}
            onClick={() => navigate(`/coding/${fileId}/edit`)}
          >
            Edit
          </Button>
          {coding.verificationStatus === 'PENDING' && (
            <Button
              variant="contained"
              startIcon={<Verified />}
              onClick={() => setOpenVerifyDialog(true)}
            >
              Verify
            </Button>
          )}
          <Button
            startIcon={<Download />}
            variant="outlined"
          >
            Export
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Patient & File Information */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Patient Information" />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  File Number
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {mrdFile?.fileNumber}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Patient Name
                </Typography>
                <Typography variant="body1">
                  {mrdFile?.patientName}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Age / Gender
                </Typography>
                <Typography variant="body1">
                  {mrdFile?.age} years / {mrdFile?.gender}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Admission Date
                </Typography>
                <Typography variant="body1">
                  {mrdFile?.admissionDate ? format(new Date(mrdFile.admissionDate), 'MMM dd, yyyy') : '-'}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Discharge Date
                </Typography>
                <Typography variant="body1">
                  {mrdFile?.dischargeDate ? format(new Date(mrdFile.dischargeDate), 'MMM dd, yyyy') : '-'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Coding Status */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader title="Coding Status" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Verification Status
                    </Typography>
                    <Chip
                      label={coding.verificationStatus}
                      color={getStatusColor(coding.verificationStatus)}
                      size="large"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Coded By
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
                      {coding.codedBy?.name || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {coding.codedAt ? format(new Date(coding.codedAt), 'MMM dd, yyyy HH:mm') : '-'}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Version
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                      {coding.version || 1}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              
              {coding.verificationRemarks && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Verification Remarks
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {coding.verificationRemarks}
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Primary Diagnosis */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Primary Diagnosis" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">
                    ICD-10 Code
                  </Typography>
                  <Chip
                    label={coding.primaryDiagnosis?.icd10Code}
                    color="primary"
                    variant="outlined"
                    size="large"
                  />
                </Grid>
                <Grid item xs={12} md={9}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {coding.primaryDiagnosis?.icd10Description}
                  </Typography>
                  {coding.primaryDiagnosis?.diagnosis && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Clinical: {coding.primaryDiagnosis.diagnosis}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Secondary Diagnoses */}
        {coding.secondaryDiagnoses && coding.secondaryDiagnoses.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title={`Secondary Diagnoses (${coding.secondaryDiagnoses.length})`} />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ICD-10 Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Clinical Diagnosis</TableCell>
                        <TableCell>POA</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coding.secondaryDiagnoses.map((diagnosis, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip
                              label={diagnosis.icd10Code}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{diagnosis.icd10Description}</TableCell>
                          <TableCell>{diagnosis.diagnosis}</TableCell>
                          <TableCell>
                            <Chip
                              label={diagnosis.presentOnAdmission ? 'Yes' : 'No'}
                              size="small"
                              color={diagnosis.presentOnAdmission ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Procedures */}
        {coding.procedures && coding.procedures.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title={`Procedures (${coding.procedures.length})`} />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ICD-10-PCS Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Surgeon</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coding.procedures.map((procedure, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Chip
                              label={procedure.icd10pcsCode}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{procedure.description}</TableCell>
                          <TableCell>
                            {procedure.procedureDate ? format(new Date(procedure.procedureDate), 'MMM dd, yyyy') : '-'}
                          </TableCell>
                          <TableCell>{procedure.surgeon}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* DRG Information */}
        {coding.drgGroup && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="DRG Information" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      DRG Code
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {coding.drgGroup.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Weight
                    </Typography>
                    <Typography variant="h5" color="secondary">
                      {coding.drgGroup.weight}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {coding.drgGroup.description}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* APR-DRG Information */}
        {coding.aprDrg && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="APR-DRG Information" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      APR-DRG Code
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {coding.aprDrg.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Severity Level
                    </Typography>
                    <Chip
                      label={`Level ${coding.aprDrg.severityOfIllness}`}
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Risk of Mortality
                    </Typography>
                    <Chip
                      label={`Level ${coding.aprDrg.riskOfMortality}`}
                      color="warning"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Audit Trail */}
        {auditTrail.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Audit Trail" />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell>Field</TableCell>
                        <TableCell>User</TableCell>
                        <TableCell>Changes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditTrail.map((entry, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {entry.timestamp ? format(new Date(entry.timestamp), 'MMM dd, yyyy HH:mm') : '-'}
                          </TableCell>
                          <TableCell>
                            <Chip label={entry.action} size="small" />
                          </TableCell>
                          <TableCell>{entry.field}</TableCell>
                          <TableCell>{entry.by?.name || 'System'}</TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {entry.oldValue} → {entry.newValue}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Verification Dialog */}
      <Dialog open={openVerifyDialog} onClose={() => setOpenVerifyDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Verify Medical Coding</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Verification Status</InputLabel>
              <Select
                value={verificationData.status}
                label="Verification Status"
                onChange={(e) => setVerificationData(prev => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="VERIFIED">Verified</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="QUERY_RAISED">Query Raised</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks"
              value={verificationData.remarks}
              onChange={(e) => setVerificationData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder="Enter verification remarks..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVerifyDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleVerify}>
            Verify
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

